import { useEffect, useState } from 'react'
import { CheckCircle2, Info, ShieldAlert, X, XCircle } from 'lucide-react'

import type { FlashMessage } from '@/types/shared'
import { cn } from '@/lib/cn'

type FlashStackProps = {
  flash: Partial<Record<FlashMessage['type'], string>>
}

type InternalFlash = FlashMessage & { id: string }

const iconMap = {
  notice: Info,
  success: CheckCircle2,
  alert: ShieldAlert,
  error: XCircle,
}

const variantStyles = {
  notice: 'border-brand-400/20 bg-brand-500/10 text-brand-100',
  success: 'border-success-400/30 bg-success-500/15 text-success-100',
  alert: 'border-warning-400/30 bg-warning-500/15 text-warning-100',
  error: 'border-danger-400/30 bg-danger-500/15 text-danger-100',
} as const

export function FlashStack({ flash }: FlashStackProps) {
  const [items, setItems] = useState<InternalFlash[]>([])

  useEffect(() => {
    const next: InternalFlash[] = Object.entries(flash ?? {})
      .filter(([, message]) => message)
      .map(([type, message]) => ({
        id: `${type}-${message}`,
        type: type as FlashMessage['type'],
        message: message as string,
      }))

    setItems(next)
  }, [flash])

  if (items.length === 0) return null

  return (
    <div className="fixed left-1/2 top-6 z-50 flex w-full max-w-xl -translate-x-1/2 flex-col gap-3">
      {items.map((item) => {
        const Icon = iconMap[item.type]
        return (
          <div
            key={item.id}
            className={cn('glass-panel flex items-start gap-3 rounded-2xl border px-4 py-3 shadow-elevated', variantStyles[item.type])}
          >
            <Icon className="mt-1 h-5 w-5" />
            <div className="flex flex-1 flex-col">
              <p className="text-sm font-medium leading-5 text-white">{item.message}</p>
            </div>
            <button
              type="button"
              className="rounded-full p-1 text-white/60 transition hover:text-white"
              onClick={() => setItems((prev) => prev.filter((flashItem) => flashItem.id !== item.id))}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )
      })}
    </div>
  )
}
